import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

// Standalone axios instance — no auth cookies/interceptors so it works for all users
const supportApi = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  timeout: 15000,
});

// Pages where the widget appears — an allowlist, not a blocklist.
//
// The assistant answers as LawFusion's product support and sends legal
// questions to Research Mode or Debate Mode, which is only sensible advice for
// an attorney or paralegal. So it belongs on the professional side and nowhere
// else, and a new consumer page should have to opt in rather than inherit it.
//
// Left out on purpose: "/" (the audience splitter, where the visitor has not
// said which side they are on yet), "/ask" and "/find-attorney" (consumer),
// the Stripe result pages, "/unsubscribe", the shared legal boilerplate at
// "/privacy-policy" and "/terms-of-service", and the 404.
const ATTORNEY_PATHS = [
  "/attorneys",
  "/legacy-landing",
  "/new-landing",
  "/login",
  "/signup",
  "/signup2",
  "/forgot-password",
  "/reset-password",
  "/dashboard",
  "/research",
  "/debate",
  "/debate-mode",
  "/oldDebate",
  "/pricing",
  "/faq",
  "/about",
  "/directory", // covers /directory/verify and /directory/activate
  "/my-listing",
  "/settings",
  "/complete-profile",
  "/admin",
];

function SupportChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const messagesRef = useRef(null);
  const inputRef = useRef(null);
  const conversationRef = useRef([]);

  // Read from the router rather than window.location: popstate does not fire
  // on a pushState navigation, so the old listener never noticed a move
  // between pages and the widget stayed on whatever it had decided at mount.
  const { pathname } = useLocation();
  const isShown = ATTORNEY_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );

  // Lets page chrome make room for the bubble: on phones it sat on top of
  // the footer's link row (QA #31). See Footer.css.
  useEffect(() => {
    if (!isShown) return undefined;
    document.body.classList.add("lf-has-support-widget");
    return () => document.body.classList.remove("lf-has-support-widget");
  }, [isShown]);

  // Auto-scroll messages
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 280);
    }
  }, [isOpen]);

  const openChat = useCallback(() => {
    setIsOpen(true);
    if (messages.length === 0) {
      setMessages([
        {
          role: "bot",
          text: "👋 Hi! I'm the LawFusion assistant.\n\nI can help with pricing, features, upgrading, cancelling, AI Actions, the Free Directory, Featured listings, and how the platform works.\n\nFor legal analysis questions, please use **Research Mode** or **Debate Mode**.",
        },
      ]);
    }
  }, [messages.length]);

  const sendMessage = useCallback(
    async (text) => {
      if (!text?.trim() || isLoading) return;
      const userMsg = text.trim();
      setInput("");
      setMessages((prev) => [...prev, { role: "user", text: userMsg }]);
      conversationRef.current.push({ role: "user", content: userMsg });
      setIsLoading(true);

      try {
        const res = await supportApi.post("/support-chat", {
          messages: conversationRef.current,
        });
        const reply =
          res.data?.reply ||
          "I'm not sure about that. Please email support@lawfusion.ai and we'll help right away.";
        conversationRef.current.push({ role: "assistant", content: reply });
        setMessages((prev) => [...prev, { role: "bot", text: reply }]);
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: "bot",
            text: "Sorry, something went wrong on my end. Please email **support@lawfusion.ai** and our team will get you sorted quickly.",
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading],
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const chips = [
    { label: "Free trial details", q: "What does the free trial include?" },
    { label: "How to upgrade", q: "How do I upgrade my plan?" },
    { label: "How to cancel", q: "How do I cancel my subscription?" },
    { label: "AI Actions explained", q: "What is an AI Action?" },
    { label: "Research vs Debate", q: "What is the difference between Research Mode and Debate Mode?" },
    { label: "Free Directory", q: "How do I join the Free Directory?" },
    { label: "Featured Listings", q: "How do I get a Featured listing?" },
    { label: "Contact support", q: "How do I contact support?" },
  ];

  // Simple markdown-like rendering. Escaping runs first and the link pattern
  // only accepts http(s) targets, so nothing the model returns can inject
  // markup through the dangerouslySetInnerHTML below.
  const renderText = (text) => {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
      )
      .replace(/\n/g, "<br/>");
  };

  if (!isShown) return null;

  return (
    <>
      <style>{`
        .lf-widget-bubble {
          position: fixed;
          bottom: 24px; right: 24px;
          width: 58px; height: 58px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          background: linear-gradient(135deg, #2E5BFF, #1A3DBE);
          box-shadow: 0 8px 24px rgba(46,91,255,0.40), 0 2px 8px rgba(0,0,0,0.12);
          display: flex; align-items: center; justify-content: center;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          z-index: 9999;
        }
        .lf-widget-bubble:hover {
          transform: scale(1.07);
          box-shadow: 0 12px 32px rgba(46,91,255,0.50);
        }
        .lf-widget-bubble svg { width: 26px; height: 26px; fill: white; }
        .lf-widget-bubble .lf-tooltip {
          position: absolute; right: 66px;
          background: #1F2937; color: white;
          font-size: 12px; font-weight: 500;
          padding: 5px 10px; border-radius: 6px;
          white-space: nowrap; opacity: 0; pointer-events: none;
          transition: opacity 0.18s ease;
        }
        .lf-widget-bubble:hover .lf-tooltip { opacity: 1; }
        .lf-widget-window {
          position: fixed;
          bottom: 96px; right: 24px;
          width: 368px;
          background: #F6F8FC;
          border-radius: 16px;
          box-shadow: 0 20px 48px rgba(0,0,0,0.16), 0 4px 16px rgba(0,0,0,0.08);
          overflow: hidden;
          display: flex; flex-direction: column;
          max-height: 580px;
          z-index: 9998;
          opacity: 0; transform: translateY(14px) scale(0.97);
          pointer-events: none;
          transition: opacity 0.22s ease, transform 0.22s ease;
        }
        .lf-widget-window.lf-open {
          opacity: 1; transform: translateY(0) scale(1);
          pointer-events: all;
        }
        .lf-widget-header {
          background: linear-gradient(135deg, #2E5BFF, #1A3DBE);
          color: white; padding: 16px 18px;
          display: flex; align-items: center; justify-content: space-between;
          flex-shrink: 0;
        }
        .lf-widget-header-left { display: flex; align-items: center; gap: 10px; }
        .lf-widget-header-icon {
          width: 36px; height: 36px;
          background: rgba(255,255,255,0.18);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
        }
        .lf-widget-header-icon svg { width: 18px; height: 18px; fill: white; }
        .lf-widget-header-title { font-size: 15px; font-weight: 600; }
        .lf-widget-header-sub {
          font-size: 11.5px; opacity: 0.82; margin-top: 2px;
          display: flex; align-items: center; gap: 5px;
        }
        .lf-online-dot {
          width: 7px; height: 7px; background: #4ADE80;
          border-radius: 50%; display: inline-block;
        }
        .lf-close-btn {
          background: none; border: none; cursor: pointer;
          color: rgba(255,255,255,0.75); padding: 4px; border-radius: 6px;
          display: flex; align-items: center;
          transition: background 0.15s, color 0.15s;
        }
        .lf-close-btn:hover { background: rgba(255,255,255,0.15); color: white; }
        .lf-close-btn svg { width: 18px; height: 18px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; }
        .lf-widget-messages {
          flex: 1; overflow-y: auto;
          padding: 16px;
          display: flex; flex-direction: column; gap: 10px;
          min-height: 0; max-height: 300px;
          scroll-behavior: smooth;
        }
        .lf-widget-messages::-webkit-scrollbar { width: 4px; }
        .lf-widget-messages::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 4px; }
        .lf-msg-row { display: flex; align-items: flex-end; gap: 8px; animation: lfFadeUp 0.18s ease; }
        .lf-msg-row.lf-user { flex-direction: row-reverse; }
        @keyframes lfFadeUp {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .lf-avatar {
          width: 28px; height: 28px; flex-shrink: 0;
          background: linear-gradient(135deg, #2E5BFF, #1A3DBE);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
        }
        .lf-avatar svg { width: 13px; height: 13px; fill: white; }
        .lf-msg-bubble {
          max-width: 80%; padding: 10px 13px;
          font-size: 13.5px; line-height: 1.55;
          border-radius: 14px; word-break: break-word;
        }
        .lf-msg-row.lf-bot .lf-msg-bubble {
          background: white; color: #1F2937;
          border: 1px solid #E2E8F0;
          border-bottom-left-radius: 4px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        }
        .lf-msg-row.lf-user .lf-msg-bubble {
          background: linear-gradient(135deg, #2E5BFF, #1A3DBE);
          color: white; border-bottom-right-radius: 4px;
        }
        .lf-msg-bubble a { color: #2E5BFF; text-decoration: underline; }
        .lf-msg-row.lf-user .lf-msg-bubble a { color: #bfdbfe; }
        .lf-typing { display: flex; align-items: center; gap: 5px; padding: 12px 14px; }
        .lf-typing span {
          width: 7px; height: 7px; background: #94A3B8; border-radius: 50%;
          animation: lfTypeBounce 1.1s infinite ease-in-out;
        }
        .lf-typing span:nth-child(2) { animation-delay: 0.18s; }
        .lf-typing span:nth-child(3) { animation-delay: 0.36s; }
        @keyframes lfTypeBounce {
          0%,60%,100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
        .lf-chips {
          padding: 6px 14px 12px;
          display: flex; flex-wrap: wrap; gap: 6px;
          border-top: 1px solid #E9EEF6;
          flex-shrink: 0; background: #F6F8FC;
        }
        .lf-chip {
          border: 1px solid #D6DBE7; background: white; color: #1F2937;
          padding: 6px 12px; border-radius: 18px;
          font-size: 12.5px; cursor: pointer;
          transition: background 0.15s, border-color 0.15s, color 0.15s;
          font-family: inherit;
        }
        .lf-chip:hover { background: #EEF2FF; border-color: #A5B4FC; color: #2E5BFF; }
        .lf-input-row {
          display: flex; border-top: 1px solid #E2E8F0;
          background: white; flex-shrink: 0;
        }
        .lf-input {
          flex: 1; padding: 12px 14px;
          border: none; outline: none;
          font-size: 13.5px; font-family: inherit;
          color: #1F2937; background: transparent;
          resize: none; max-height: 80px; line-height: 1.45;
        }
        .lf-input::placeholder { color: #9CA3AF; }
        .lf-send-btn {
          width: 48px; border: none;
          background: linear-gradient(135deg, #2E5BFF, #1A3DBE);
          color: white; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: opacity 0.15s; flex-shrink: 0;
        }
        .lf-send-btn:hover { opacity: 0.88; }
        .lf-send-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .lf-send-btn svg { width: 17px; height: 17px; fill: white; }
        @media (max-width: 430px) {
          .lf-widget-window { width: calc(100vw - 16px); right: 8px; bottom: 88px; }
          .lf-widget-bubble { right: 16px; bottom: 20px; }
        }

        /* ── Dark mode overrides ── */
        html.dark .lf-widget-window {
          background: #1e293b;
          box-shadow: 0 20px 48px rgba(0,0,0,0.40), 0 4px 16px rgba(0,0,0,0.25);
        }
        html.dark .lf-msg-row.lf-bot .lf-msg-bubble {
          background: #334155 !important; color: #f1f5f9 !important;
          border-color: #475569 !important;
          box-shadow: 0 1px 6px rgba(0,0,0,0.25);
        }
        html.dark .lf-msg-row.lf-bot .lf-msg-bubble strong { color: #e2e8f0 !important; }
        html.dark .lf-msg-row.lf-bot .lf-msg-bubble a { color: #60a5fa !important; }
        html.dark .lf-chips {
          background: #1e293b; border-top-color: #475569;
        }
        html.dark .lf-chip {
          background: #334155 !important; color: #e2e8f0 !important;
          border-color: #475569 !important;
        }
        html.dark .lf-chip:hover {
          background: #3b4f6b !important; border-color: #6366f1 !important;
          color: #93c5fd !important;
        }
        html.dark .lf-input-row {
          background: #1e293b; border-top-color: #475569;
        }
        html.dark .lf-input {
          color: #f1f5f9 !important;
        }
        html.dark .lf-input::placeholder { color: #64748b !important; }
        html.dark .lf-widget-messages::-webkit-scrollbar-thumb { background: #475569; }
        html.dark .lf-typing span { background: #64748b; }
        html.dark .lf-widget-bubble .lf-tooltip { background: #0f172a; }
      `}</style>

      {/* Bubble */}
      <button
        className="lf-widget-bubble"
        aria-label="Ask LawFusion"
        onClick={() => (isOpen ? setIsOpen(false) : openChat())}
      >
        <span className="lf-tooltip">Ask LawFusion</span>
        <svg viewBox="0 0 24 24">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
        </svg>
      </button>

      {/* Chat Window */}
      <div
        className={`lf-widget-window ${isOpen ? "lf-open" : ""}`}
        role="dialog"
        aria-label="LawFusion Support"
      >
        {/* Header */}
        <div className="lf-widget-header">
          <div className="lf-widget-header-left">
            <div className="lf-widget-header-icon">
              <svg viewBox="0 0 24 24">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
              </svg>
            </div>
            <div>
              <div className="lf-widget-header-title">Ask LawFusion</div>
              <div className="lf-widget-header-sub">
                <span className="lf-online-dot" />
                Typically replies instantly
              </div>
            </div>
          </div>
          <button
            className="lf-close-btn"
            aria-label="Close"
            onClick={() => setIsOpen(false)}
          >
            <svg viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="lf-widget-messages" ref={messagesRef} role="log" aria-live="polite">
          {messages.map((msg, i) => (
            <div key={i} className={`lf-msg-row ${msg.role === "user" ? "lf-user" : "lf-bot"}`}>
              {msg.role === "bot" && (
                <div className="lf-avatar">
                  <svg viewBox="0 0 24 24">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
                  </svg>
                </div>
              )}
              <div
                className="lf-msg-bubble"
                dangerouslySetInnerHTML={{ __html: renderText(msg.text) }}
              />
            </div>
          ))}
          {isLoading && (
            <div className="lf-msg-row lf-bot">
              <div className="lf-avatar">
                <svg viewBox="0 0 24 24">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
                </svg>
              </div>
              <div className="lf-msg-bubble">
                <div className="lf-typing">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chips */}
        <div className="lf-chips">
          {chips.map((chip) => (
            <button
              key={chip.label}
              className="lf-chip"
              onClick={() => !isLoading && sendMessage(chip.q)}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Input */}
        <div className="lf-input-row">
          <textarea
            className="lf-input"
            ref={inputRef}
            rows={1}
            placeholder="Ask a question about LawFusion..."
            maxLength={500}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="lf-send-btn"
            disabled={!input.trim() || isLoading}
            aria-label="Send"
            onClick={() => sendMessage(input)}
          >
            <svg viewBox="0 0 24 24">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}

export default SupportChatWidget;
